import { getUsers } from './api/user/list';
import { getPatientProcesses, PatientProcessesResponse } from './api/patient';
import { getPatientDetail } from './api/patient/detail';
import { getProcesses, ProcessesListQuery, ProcessesListResponse } from './api/process';
import { getPsychologists, PsychologistsListQuery, PsychologistsListResponse } from './api/psychologist';
import { getSessions, SessionsListQuery, SessionsListResponse } from './api/session/list';
import { getPsychologistSessionsTimeline, SessionsTimeline } from './api/session/timeline';
import { getSessionsByDay, SessionsByDayResponse, SessionsByDayQuery } from './api/session/by-day';
import { http } from './api/http';
import { secureHttp } from './api/secureHttp';
import { beHttp } from './api/beHttp';
import { getPsychologistWorkSchedules, PsychologistWorkSchedulesQuery, PsychologistWorkSchedulesResponse } from './api/schedule';
import type { Session } from '../types/session';
import { mapSessionResponse, RawSessionItem } from '../utils/sessionMapper';
import { PsychologistFreeScheduleStatus, Slot } from '../components/SessionDebug';
import type { JourneyBotRequest, JourneyBotResponse } from '../types/journey';
import type { DerivationFormValues } from '../components/psychologist/patients/derivation/derivationFormSchema';


export type { SessionsListResponse, SessionsListQuery, SessionsByDayResponse, SessionsByDayQuery };

export type DerivationStatus = 'IN_PROGRESS' | 'DONE' | 'DISCARDED' | 'PENDING';
export type DerivationType = 'INTERNAL' | 'EXTERNAL';
export type DerivationRequester = 'PATIENT' | 'PSYCHOLOGIST';

export interface ProcessDerivation {
  id: string;
  status: DerivationStatus;
  reason: { id: string; name: string; description?: string };
  notes?: string;
  diagnostic?: string;
  epicrisis?: string;
  suggestion?: string;
  created_at: string;
  to_psychologist?: { id: string; name?: string; surname?: string; email: string };
  from_psychologist?: { id: string; name?: string; surname?: string; email: string };
  to_first_session_date?: string;
  from_session_count?: number;
  from_process_id?: string;
  /**
   * Pendientes de exponer en api-core: la tabla `derivation` ya tiene estas columnas
   * (`type`, `requester`, `preference`) pero GET .../derivations todavía no las serializa.
   * Quedan opcionales para no romper la UI mientras el backend no las envía.
   */
  type?: DerivationType;
  requester?: DerivationRequester;
  preference?: string;
}

/**
 * Otro proceso del mismo paciente (vía `process_patient`), distinto del actual.
 * Se usa para detectar un "baja y retorno" informal (paciente que vuelve sin derivación
 * formal) y para enriquecer la sección "Proceso anterior" con fechas y sesiones efectivas
 * incluso cuando sí hubo una derivación formal.
 *
 * ENDPOINT A CONSTRUIR EN API-CORE — no existe todavía.
 * Propuesta: GET /v1/psi-platform/psychologist/patients/{processId}/previous-processes
 * Fuente de datos esperada (schema real): `process_patient` (patient → process) para listar
 * los procesos del paciente; `process.psychologist`, `process.activation_date`,
 * `process.last_session_date`, `process.status` (nombre); `process_status_history.reason`
 * → `process_status_change_reason.name` para la última transición a "Inactivo" o
 * "Proceso de baja"; y el status de cada `session` del proceso (para `session_statuses`,
 * ver `isEffectiveSessionStatus` en `utils/sessions.ts`).
 * Mientras el endpoint no exista, `getPatientPreviousProcesses` puede devolver 404/error —
 * se trata como "sin procesos anteriores" (ver catch en PatientDetailModal).
 */
export interface PatientPreviousProcess {
  process_id: string;
  psychologist?: { id: string; name?: string; surname?: string; email: string };
  activation_date?: string;
  last_session_date?: string;
  status?: string;
  /** Motivo de la última transición a "Inactivo" / "Proceso de baja" (process_status_change_reason.name) */
  status_change_reason?: string;
  /** Status de cada sesión de este proceso — para calcular sesiones efectivas en el FE */
  session_statuses?: string[];
}

export interface ScheduleSessionPayload {
  process_id: string;
  date: string;
  scheduled_by: string;
  preferences?: string[];
  psychologist_id?: string;
  appointment_type?: string;
  // Bypass de checks (sesión ya activa futura, slot disponible) — solo usado por
  // flujos de impersonación que registran sesiones históricas. Default false.
  bypass_constraints?: boolean;
}
export interface OccupationRecurrenceResponse {
  success: boolean; message: string, 
  recurrence: {
    id: string,
    psychologist: string,
    weekday: number,
    is_full_day: boolean,
    start_time: string,
    end_time: string,
    reason: string,
  }
}

export interface OccupationRecurrence {
  id: string;
  psychologist: string;
  weekday: number;
  is_full_day: boolean;
  start_time: string | null;
  end_time: string | null;
  reason: string | null;
  created_at: string;
  updated_at?: string | null;
  deleted_at?: string | null;
}

export interface GetOccupationRecurrencesResponse {
  success: boolean;
  recurrences: OccupationRecurrence[];
}

export interface UpdateOccupationRecurrencePayload {
  weekday?: number;
  is_full_day?: boolean;
  start_time?: string | null;
  end_time?: string | null;
  reason?: string | null;
  deleted_at?: boolean;
}

export interface AvailabilityParams {
  from: string;
  to: string;
  source?: string;
}

export interface ScheduleSessionResponse {
  success: boolean;
  reason?: string;
  error?: string;
  datetime?: string;
}

export default {
  baseUrl: '',
  // Users
  async usersList(): Promise<unknown> { return await getUsers(); },

  // Psychologists
  async psychologistsList(params?: PsychologistsListQuery): Promise<PsychologistsListResponse> {
    return await getPsychologists(params);
  },
  // Psychologist - notify profile changes (services/topics/approaches) — también reutilizado
  // por cualquier flujo que necesite mandar un mail de confirmación con el mismo template de marca.
  /**
   * Historial propio de "vacaciones canceladas antes de gozarse" — workaround del bug real
   * de Make que sobreescribe from/to al cancelar un período (ver memoria del proyecto y
   * `VacationCancellationSnapshot` en psiplatform-be). Solo cubre cancelaciones hechas desde
   * el panel admin de Psima — hoy es la única vía real de "Cancelar" (esta psicóloga no
   * tiene ese botón en su propio portal, solo "Modificar").
   */
  async psychologistVacationsCancelledHistory(): Promise<{
    results: Array<{ id: string; legacyVacationId: string; originalFrom: string; originalTo: string; wasFuture: boolean; cancelledAt: string; cancelledByName: string | null }>;
  }> {
    return await beHttp.get(`/api/psychologist/vacations/cancelled-history`);
  },

  /**
   * Rastro de auditoría de "Modificar" — se llama como side-effect fire-and-forget
   * justo después de que el webhook de Make confirmó la edición (mismo criterio que
   * `sendConsentMail`/`psychologistNotifyProfileChanges`). El backend correlaciona
   * por fecha (no hay id del registro nuevo que devuelva Make) — ver memoria del
   * proyecto y `VacationEditHistory` en psiplatform-be.
   */
  async psychologistVacationsLogEdit(input: {
    previousVacationId: string;
    previousFrom: string;
    previousTo: string;
    newFrom: string;
    newTo: string;
  }): Promise<{ ok: true }> {
    return await beHttp.post(`/api/psychologist/vacations/log-edit`, input);
  },

  /**
   * Ids de `psychologist_vacation` (MySQL legacy) que hay que ocultar del listado
   * principal por ser fantasmas de una cancelación (con o sin fecha real rescatada) —
   * distinto de `psychologistVacationsCancelledHistory`, que solo trae cancelaciones
   * futuras con fecha para la sección de auditoría.
   */
  async psychologistVacationsHiddenLegacyIds(): Promise<{ ids: string[] }> {
    return await beHttp.get(`/api/psychologist/vacations/hidden-legacy-ids`);
  },

  /** Historial propio de ediciones — la sección "Modificado" del tab de vacaciones. */
  async psychologistVacationsEditHistory(): Promise<{
    results: Array<{
      id: string;
      previousVacationId: string;
      previousFrom: string;
      previousTo: string;
      newFrom: string;
      newTo: string;
      editedBy: 'psychologist' | 'admin';
      editedByName: string | null;
      editedAt: string;
    }>;
  }> {
    return await beHttp.get(`/api/psychologist/vacations/edit-history`);
  },

  /**
   * Envía el formulario de "No Agendamiento" — el popup nativo (`NoAgendamientoModal.tsx`)
   * le pegaba antes DIRECTO al webhook de Make; ahora pasa por acá para que quede
   * historial real (ver NoAgendamientoService en psiplatform-be). Sin `showSuccessToast`:
   * el modal ya tiene su propia pantalla de éxito. Si el escenario de Make rechaza el envío
   * (proceso duplicado, etc.), el backend responde 409 y `beHttp` ya muestra ese mensaje
   * con un toast automático — no hace falta parseo custom acá.
   */
  async submitNoAgendamiento(input: {
    process_id: string;
    patient_email: string;
    patient_name: string;
    reason: string;
    sub_reason: string;
    follow_up_in_days: string;
    session_topics_addressed: string;
    patient_evolution: string;
    future_recommendations: string;
  }): Promise<{ ok: true }> {
    return await beHttp.post(`/api/psychologist/no-agendamiento`, input, {
      showSuccessToast: false,
    });
  },

  /**
   * Envía el wizard nativo de "Derivaciones" — `DerivationWizard.tsx` le pegaba antes
   * DIRECTO al webhook de Make; ahora pasa por acá para que quede historial completo (ver
   * DerivationSubmissionsService en psiplatform-be). Sin `showSuccessToast`: el wizard ya
   * muestra su propio toast de éxito. Si el escenario de Make rechaza el envío, el backend
   * responde 409 y `beHttp` ya muestra ese mensaje con un toast automático — no hace falta
   * parseo custom acá.
   */
  async submitDerivation(
    input: Omit<
      DerivationFormValues,
      'psychologist_email' | 'patient_email' | 'patient_name'
    >,
  ): Promise<{ ok: true }> {
    return await beHttp.post(`/api/psychologist/derivation-submissions`, input, {
      showSuccessToast: false,
    });
  },

  /**
   * Datos de identificación (paciente + psicóloga) para el Paso 1 del wizard de Derivación,
   * resueltos server-side por `processId` — reemplaza los query params `patient_email`/
   * `patient`/`psychologist_email` que antes venían en la URL (mismo esquema que ya usaba el
   * iframe de Tally, editable a mano por cualquiera). Ver DerivationSubmissionsService.
   */
  async getDerivationSubmitContext(processId: string): Promise<{
    patientEmail: string;
    patientName: string;
    psychologistEmail: string;
  }> {
    return await beHttp.get(
      `/api/psychologist/derivation-submissions/context/${encodeURIComponent(processId)}`,
    );
  },

  async psychologistNotifyProfileChanges(
    input: {
      to?: string;
      html: string;
      subject?: string;
      from?: string;
      /**
       * `team` manda el mail al equipo (TEAM_NOTIFICATIONS_TO en el backend) en vez de a
       * la psicóloga autenticada. Se usa para los avisos que el equipo tiene que
       * accionar, ej. condiciones de Orientación Vocacional aceptadas.
       */
      audience?: 'psychologist' | 'team';
    },
    toastOptions?: { successMessage?: string; showSuccessToast?: boolean },
  ): Promise<unknown> {
    return await beHttp.post(`/api/psychologist/notify-profile-changes`, input, {
      showSuccessToast: toastOptions?.showSuccessToast ?? true,
      successMessage: toastOptions?.successMessage ?? 'Se notificaron los cambios de perfil',
      errorMessage: 'No se pudo notificar los cambios',
    });
  },

  /**
   * Avisa al equipo que hay certificados pendientes de validación.
   *
   * Va a psiplatform-be con el Bearer de la psicóloga, no a api-core. El destinatario
   * sale de `MAIL_NOTIFICATIONS_TO` en el backend (no del body) y el HTML lo arma él,
   * porque los links de aprobar/rechazar llevan un token de un solo uso que sólo el
   * backend puede emitir.
   *
   * Antes esto iba a `/v1/psi-platform/psychologist/notify-profile-changes` con
   * `skipSessionAuth` y devolvía **401**: ese prefijo exige Bearer. El aviso no salía
   * desde el 2026-07-06, silenciado por un catch vacío en el llamador.
   */
  async notifyTeam(input: {
    certificates: Array<{ service: string; filename: string; documentUrl?: string }>;
  }): Promise<{ sent: boolean }> {
    return await beHttp.post(`/api/psychologist/notify-team`, input, {
      showSuccessToast: false,
      showErrorToast: false,
    });
  },

  // Sessions
  async sessionsList(params?: SessionsListQuery): Promise<SessionsListResponse> {
    return await getSessions(params);
  },

  async sessionById(sessionId: string): Promise<Session> {
    return await http.get(`/v2/sessions/${sessionId}`);
  },
  
  async psychologistSessionsList(params?: { from?: string; to?: string; take?: number; status?: string }): Promise<SessionsListResponse> {
    // Establecer valores por defecto para optimizar la carga
    const take = params?.take || 50; // Reducir a 50 por defecto
    const status = params?.status || 'active,settled,incomplete';
    
    // Si no se proporciona un rango de fechas, usar la semana actual
    let from = params?.from;
    let to = params?.to;
    
    if (!from || !to) {
      const today = new Date();
      const weekStart = new Date(today);
      weekStart.setDate(today.getDate() - today.getDay() + 1); // Lunes de esta semana
      
      const weekEnd = new Date(weekStart);
      weekEnd.setDate(weekStart.getDate() + 6); // Domingo
      
      const formatDate = (d: Date) => {
        return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')} 00:00:00`;
      };
      
      from = from || formatDate(weekStart);
      to = to || formatDate(weekEnd);
    }
    
    // La API ahora devuelve un array de objetos de sesión directamente
    let results: RawSessionItem[] = [];
    try {
      // Añadir un parámetro de timestamp para evitar respuestas 304 Not Modified
      const timestamp = Date.now();
      
      results = await beHttp.get<RawSessionItem[]>(`/api/psychologist/sessions`, {
        from,
        to,
        take,
        status,
        _t: timestamp, // Parámetro para evitar caché
      });
    } catch (error) {
      // Error obteniendo sesiones
      results = [];
    }
    
    // Asegurarse de que results siempre sea un array
    if (!Array.isArray(results)) {
      // La respuesta de sesiones no es un array, usando array vacío
      results = [];
    }
    
    // Para depuración
    // API respuesta sesiones
    
    // Crear estructura compatible con la interfaz esperada
    const resp = {
      results: results,
      metadata: { total: results.length }
    };

    // Asegurarse de que resp.results es un array
    const items = Array.isArray(resp.results) ? resp.results : [];
    
    // Transformar los datos a formato Session
    const data: Session[] = items.map(mapSessionResponse);
    const total = resp.metadata?.total ?? data.length;
    return { data, total, metadata: resp.metadata };
  },

  async psychologistSessionsTimeline(opts?: { take?: number; from?: string; to?: string }): Promise<SessionsTimeline> {
    return await getPsychologistSessionsTimeline(opts);
  },
  
  // Nuevo método para obtener sesiones agrupadas por día
  async psychologistSessionsByDay(params?: SessionsByDayQuery): Promise<SessionsByDayResponse> {
    return await getSessionsByDay(params);
  },

  // Processes
  async processesList(params?: ProcessesListQuery): Promise<ProcessesListResponse> {
    return await getProcesses(params);
  },

  // Patient Processes
  async patientProcessesList(): Promise<PatientProcessesResponse> {
    return await getPatientProcesses();
  },
  async patientDetail(id: string) {
    return await getPatientDetail(id);
  },

  // Schedules
  async psychologistWorkSchedules(params?: PsychologistWorkSchedulesQuery): Promise<PsychologistWorkSchedulesResponse> {
    return await getPsychologistWorkSchedules(params);
  },

  // Psychologist - extra endpoints
  async psychologistMessages(): Promise<unknown> {
    return await beHttp.get(`/api/psychologist/messages`);
  },
  async psychologistLogs(params?: Record<string, unknown>): Promise<unknown> {
    return await beHttp.get(`/api/psychologist/logs`, params);
  },
  async psychologistStatusReasons(): Promise<unknown> {
    return await http.get(`/v2/psychologists/status/reasons`);
  },
  async psychologistPatients(): Promise<{ results: Array<{ id: string; process: string; name: string; surname: string; email: string; process_type?: string; phone_last_four?: string; timezone?: string; nationality?: string; scheduling_eligible?: boolean }>; metadata?: { total?: number } }> {
    return await beHttp.get(`/api/psychologist/patients`);
  },
  async psychologistBlockades(): Promise<{ results: Array<{ id: string; from: string; to: string | null; active: boolean; notes: string | null; reason: { id: string; name: string; description: string | null; type: string | null } | null }> }> {
    return await beHttp.get(`/api/psychologist/blockades`);
  },
  async psychologistPreferences(psychologistId: string): Promise<Array<{ id: string; psychologist?: { process_limit_preferred?: number | null } }> | { results: Array<{ id: string; psychologist?: { process_limit_preferred?: number | null } }> }> {
    return await secureHttp.get(`/v1/psychologists/preferences`, { psychologist: psychologistId, skip: 0, take: 100 });
  },
  async updatePsychologistPatientLimit(
    psychologistId: string,
    payload: { process_limit: number; process_limit_preferred: number; justification_id?: string },
  ): Promise<{ id: string; process_limit: number; process_limit_preferred: number }> {
    return await beHttp.patch(`/api/psychologist/patient-limit`, payload);
  },
  /**
   * Registra un intento de cambio del límite preferido de pacientes (Supabase).
   * `outcome` distingue 'confirmed' (fricción autogestionable con motivo — default si se
   * omite), 'blocked' (bloqueo duro ≥30%, el valor nunca se aplica) y 'cancelled' (abrió el
   * modal de fricción y canceló sin confirmar). `reason` solo aplica a 'confirmed'.
   * El `id` de la respuesta hay que pasarlo como `justification_id` en el PATCH real
   * (`updatePsychologistPatientLimit`) para que el backend ligue el mail a administracion@ con
   * el motivo exacto de este cambio — ver comentario en profile-extras.service.ts.
   */
  async psychologistPatientLimitJustification(input: {
    psychologist_id: string;
    previous_value: number;
    new_value: number;
    drop_percentage: number;
    outcome?: 'confirmed' | 'blocked' | 'cancelled';
    reason?: string;
  }): Promise<{
    id: string;
    psychologist_id: string;
    previous_value: number;
    new_value: number;
    drop_percentage: number;
    outcome: 'confirmed' | 'blocked' | 'cancelled';
    reason: string | null;
    created_at: string;
  }> {
    return await beHttp.post(`/api/psychologist/patient-limit-justifications`, input);
  },
  async psychologistServices(): Promise<Array<{ id: string; service: { id: string; name: string; description?: string } }>> {
    return await beHttp.get(`/api/psychologist/services`);
  },
  /**
   * @deprecated Use scheduleSession instead. This endpoint will be removed in a future version.
   */
  async scheduleSessionDirect(input: { process_id: string; date: string; scheduled_by: string; notes?: string; appointment_type?: string }): Promise<unknown> {
    return await secureHttp.post(`/v1/psi-platform/session/direct`, input);
  },
  
  async scheduleSession(input: ScheduleSessionPayload): Promise<ScheduleSessionResponse> {
    return await http.post<ScheduleSessionResponse, ScheduleSessionPayload>(`/v2/sessions/schedule`, input);
  },
  async psychologistVacations(params?: Record<string, unknown>): Promise<unknown> {
    return await http.get(`/v2/psychologists/vacations`, params);
  },
  async psychologistScheduledVirtualRanking(): Promise<unknown> {
    return await http.post(`/v2/psychologists/scheduled/virtual-ranking`);
  },
  async changeSlotStatus(psychologist_id: string, status: PsychologistFreeScheduleStatus, from: string, to: string, reason?: string | null): Promise<{ success: boolean; message: string }> {
    return await http.patch(`/v2/psychologists/${psychologist_id}/availabilities/status`, { status, from, to, reason });
  },
  async changeFullDaySlotStatus(psychologist_id: string, status: PsychologistFreeScheduleStatus, date: string, reason?: string | null): Promise<{ success: boolean; message: string }> {
    return await http.post(`/v2/psychologists/${psychologist_id}/availabilities/occupations/full-day`, { status, date, reason });
  },
  async createOccupationRecurrence(psychologist_id: string, weekday: number, is_full_day: boolean, start_time: string, end_time: string, reason?: string | null): Promise<OccupationRecurrenceResponse> {
    return await http.post(`/v2/psychologists/${psychologist_id}/availabilities/occupations/recurrence`, { weekday, is_full_day, start_time, end_time, reason });
  },
  async getOccupationRecurrences(psychologist_id: string): Promise<GetOccupationRecurrencesResponse> {
    return await http.get(`/v2/psychologists/${psychologist_id}/availabilities/occupations/recurrence`);
  },
  async updateOccupationRecurrence(psychologist_id: string, recurrence_id: string, payload: UpdateOccupationRecurrencePayload): Promise<OccupationRecurrenceResponse> {
    return await http.patch(`/v2/psychologists/${psychologist_id}/availabilities/occupations/recurrence/${recurrence_id}`, payload);
  },
  async psychologistAvailability(params: AvailabilityParams): Promise<Array<Slot>> {
    return await secureHttp.get(`/v1/psi-platform/psychologist/availability`, { ...params });
  },
  
  // Patients - aggregated process/sessions info
  async patientsInformation(params?: { page?: number; per_page?: number; status?: string; therapy_type?: string }): Promise<{
    results: Array<{
      id: string;
      process_id?: string | null;
      name: string | null;
      surname: string | null;
      therapy_type?: string | null;
      last_session_date?: string | null;
      next_session_date?: string | null;
      has_derivation?: boolean;
      scheduling_eligible?: boolean;
      status_buckets?: Array<"first" | "active" | "inactive" | "withoutsession">;
      session_total?: number | null;
      /** Sesiones reales alguna vez tenidas (liquidadas/activas/incompletas/free, sin canceladas).
       *  Solo lo devuelve psiplatform-be; ausente en un backend anterior. */
      sessions_non_cancelled?: number;
    }>;
    metadata?: {
      total?: number;
      pages?: number;
      current_page?: number;
      per_page?: number;
      has_next_page?: boolean;
      has_prev_page?: boolean;
    };
  }> {
    const page = params?.page ?? 1;
    const per_page = params?.per_page ?? 10;
    const status = params?.status;
    const therapy_type = params?.therapy_type;
    const query: Record<string, string | number> = { page, per_page };
    if (status) query.status = status;
    if (therapy_type) query.therapy_type = therapy_type;
    return await beHttp.get(`/api/psychologist/patients/process-sessions-data`, query);
  },

  // Patient detail by process id
  async psychologistPatientByProcess(processId: string): Promise<unknown> {
    return await beHttp.get(`/api/psychologist/patients/${encodeURIComponent(processId)}`);
  },

  async getProcessDerivations(processId: string): Promise<ProcessDerivation[]> {
    const res = await beHttp.get<{ items: ProcessDerivation[] }>(`/api/psychologist/patients/${encodeURIComponent(processId)}/derivations`);
    return res?.items ?? [];
  },

  // Implementado en el backend nuevo (construido en psiplatform-be).
  async getPatientPreviousProcesses(processId: string): Promise<PatientPreviousProcess[]> {
    const res = await beHttp.get<{ items: PatientPreviousProcess[] }>(`/api/psychologist/patients/${encodeURIComponent(processId)}/previous-processes`);
    return res?.items ?? [];
  },

  async getPatientSessionNotes(processId: string): Promise<Array<{ session_id: string; date: string; notes: string; process_id: string; psychologist_name: string; psychologist_discharged: boolean }>> {
    const res = await beHttp.get<{ items: Array<{ session_id: string; date: string; notes: string; process_id: string; psychologist_name: string; psychologist_discharged: boolean }> }>(`/api/psychologist/patients/${encodeURIComponent(processId)}/session-notes`);
    return res?.items ?? [];
  },

  /**
   * Historial COMPLETO de sesiones de un/a paciente — implementado en psiplatform-be.
   */
  async getPatientFullSessionHistory(processId: string): Promise<Array<{
    session_id: string;
    date: string;
    status: string;
    process_id: string;
    psychologist_id?: string | null;
    psychologist_name: string;
  }>> {
    const res = await beHttp.get<{ items: Array<{ session_id: string; date: string; status: string; process_id: string; psychologist_id?: string | null; psychologist_name: string }> }>(`/api/psychologist/patients/${encodeURIComponent(processId)}/full-session-history`);
    return res?.items ?? [];
  },
  async psychologistPatientSessionsHistory(processId: string): Promise<Array<{ sessionId?: string; session_id?: string; date: string; notes?: string | null; process_id?: string; status?: string; patient_timezone?: string }>> {
    return await beHttp.get<Array<{ sessionId?: string; session_id?: string; date: string; notes?: string | null; process_id?: string; status?: string; patient_timezone?: string }>>(
      `/api/psychologist/sessions/sessions-history`,
      { process_id: processId },
    );
  },

  /** Busca el timezone del paciente desde las sesiones del psicólogo */
  async getPatientTimezoneFromSessions(processId: string): Promise<string | null> {
    try {
      const now = new Date();
      const past = new Date(now);
      past.setFullYear(past.getFullYear() - 1);
      const future = new Date(now);
      future.setFullYear(future.getFullYear() + 1);
      const fmt = (d: Date) =>
        `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')} 00:00:00`;

      const sessions = await beHttp.get<Array<{
        process?: { id?: string } | string;
        patients?: Array<{ timezone?: string }>;
      }>>(`/api/psychologist/sessions`, {
        from: fmt(past),
        to: fmt(future),
        take: 200,
        _t: Date.now(),
      });
      if (!Array.isArray(sessions)) return null;
      for (const s of sessions) {
        const pid = typeof s.process === 'string' ? s.process : s.process?.id;
        if (pid === processId && s.patients?.[0]?.timezone) {
          return s.patients[0].timezone;
        }
      }
      return null;
    } catch {
      return null;
    }
  },
  
  async updatePsychologistProfile(input: Partial<{
    name: string;
    surname: string;
    birthdate: string;
    phone: string;
    services: string[];
    // Keys = clinical_preferences.type (topic, specialization, y cualquier
    // otro type del enum ClinicalPreferenceType de api-core) — no el filterKey
    // de be-make, que puede diferir (ver "topic_preference" -> type "topic").
    preferences: Record<string, string[]>;
    timezone: string | null;
  }>): Promise<unknown> {
    return await beHttp.put(
      `/api/psychologist/profile`,
      input,
      {
        loadingMessage: 'Guardando perfil…',
        successMessage: 'Perfil actualizado',
        errorMessage: 'No se pudo actualizar el perfil',
        showSuccessToast: true,
        showErrorToast: true,
      }
    );
  },
  async updateSessionNote(sessionId: string, noteContent: string): Promise<unknown> {
    return await beHttp.put(
      `/api/psychologist/sessions/${encodeURIComponent(sessionId)}/note-data`,
      { note_content: noteContent },
      {
        loadingMessage: 'Guardando registro…',
        successMessage: 'Registro guardado',
        errorMessage: 'No se pudo guardar el registro',
        showSuccessToast: true,
        showErrorToast: true,
      }
    );
  },

  /**
   * Recordatorio personal de la psicóloga sobre un paciente — texto libre, privado por
   * psicóloga (no por sesión de acceso), NO es historia clínica ni nota de sesión.
   * Implementado en psiplatform-be (tabla `patient_personal_note`).
   */
  async getPatientPersonalNote(processId: string): Promise<{ content: string }> {
    return await beHttp.get<{ content: string }>(
      `/api/psychologist/patients/${encodeURIComponent(processId)}/personal-note`,
    );
  },
  async updatePatientPersonalNote(processId: string, content: string): Promise<{ content: string }> {
    return await beHttp.put<{ content: string }>(
      `/api/psychologist/patients/${encodeURIComponent(processId)}/personal-note`,
      { content },
      {
        loadingMessage: 'Guardando…',
        successMessage: 'Nota personal guardada',
        errorMessage: 'No se pudo guardar la nota personal',
        showSuccessToast: true,
        showErrorToast: true,
      }
    );
  },

  // Psychologist profile
  async psychologistProfile(): Promise<{
    name?: string;
    surname?: string;
    birthdate?: string;
    phone?: string;
    services?: string[];
    // Agrupado por clinical_preferences.type (topic, specialization, sex, ...)
    // — igual que ahora generaliza api-core (ClinicalPreferenceType). Una key
    // ausente = sin preferencias de ese type, no un array vacío explícito.
    preferences?: Record<string, string[]>;
    created_at?: string;
    current_process_count?: number;
    process_limit?: number;
    process_limit_preferred?: number;
    status_id?: string;
    ranking_category?: string;
    timezone?: string | null;
    /** Canal de contacto ya resuelto server-side (fee_type=range o segmento nuevo en
     *  psychologist_psichat_inbox) — ver useContactChannel. */
    uses_admin_contact_channel?: boolean;
    /** `true` solo para el segmento nuevo, nunca para fee_type=range — gatilla el popup
     *  de aviso único (ver ContactChannelUpdatedModal). */
    show_contact_channel_notice?: boolean;
    /** Tarifa (`psychologist.fee_type`): 'range' | 'usd_v1' | 'usd_v2' | 'ars' | null. Ausente en un
     *  backend anterior al campo — ver usePsychologistFeeType. */
    fee_type?: string | null;
  }> {
    return await beHttp.get(`/api/psychologist/profile`);
  },
  /**
   * Catálogo dinámico de "Sobre tu atención" (temáticas, enfoques, y
   * cualquier otro filtro que el equipo habilite para psi-platform desde
   * quieroserpaciente) — reemplaza el array hardcodeado que tenía este
   * archivo. Misma fuente que ya usa el backoffice de psicólogas (be-make),
   * filtrada por la config de visibilidad propia de psi-platform.
   */
  async psychologistPreferencesCatalog(): Promise<
    Array<{
      filterKey: string;
      label: string;
      /** clinical_preferences.type real — key para guardar este grupo en updatePsychologistProfile. */
      type: string;
      /** false = api-core no sabe persistir este type todavía; se muestra de solo lectura. */
      writable: boolean;
      options: Array<{ id: string; name: string; description: string; active: boolean }>;
    }>
  > {
    return await beHttp.get(`/api/psychologist/profile/preferences-catalog`);
  },
  async cancelSession(sessionId: string, payload?: { motive_id?: string; note?: string; initiated_by?: string; cancellation_type?: string }): Promise<unknown> {
    // cancelSession es operación CORE — sigue en api-core (escribe en MySQL).
    return await secureHttp.delete(`/v1/psi-platform/session/${sessionId}`, {
      motive_id: payload?.motive_id,
      note: payload?.note,
      initiated_by: payload?.initiated_by,
      cancellation_type: payload?.cancellation_type,
    });
  },
  async getCancellationMotives(): Promise<{ id: string; name: string; description?: string; action?: string; applies_to?: string }[]> {
    // Catálogo de motivos — queda en api-core junto con cancelSession.
    return await secureHttp.get(`/v1/psi-platform/session/cancellation-motive`);
  },
  async reportSessionAbsence(sessionId: string): Promise<{ id: string; sessionId: string; status: 'attempt' | 'confirmed'; createdAt: string }> {
    // Dato NUEVO (no existe en MySQL de api-core) → psiplatform-be, tabla propia
    // SessionAbsenceReport en Supabase. Se llama cada vez que la psicóloga abre el
    // modal de "Reportar ausencia", para dejar un registro agregado de todos los
    // intentos del equipo. psiplatform-be valida ownership de la sesión contra la
    // psicóloga autenticada antes de escribir.
    return await beHttp.post(`/api/psychologist/sessions/${sessionId}/absence-report`, {});
  },
  async confirmSessionAbsence(sessionId: string): Promise<{ id: string; sessionId: string; status: 'attempt' | 'confirmed'; createdAt: string }> {
    // Pasa el reporte de ausencia más reciente de esta sesión ('attempt') a
    // 'confirmed'. Se llama al confirmar "Ya avisé a CX, salir de la sesión"
    // en el modal — gateado por tiempo del lado del frontend (35 min desde el
    // inicio agendado, la tolerancia institucional real).
    return await beHttp.patch(`/api/psychologist/sessions/${sessionId}/absence-report/confirm`, {});
  },
  async notifyPatientAbsence(sessionId: string): Promise<{ outcome: 'sent' | 'already_sent' | 'skipped' | 'error' }> {
    // Se llama al hacer click en "Reportar ausencia" — psiplatform-be le avisa a
    // psima (server-side, la x-api-key nunca viaja al browser) para que le mande
    // al PACIENTE el WhatsApp "psico_esperando_paciente". Detrás de
    // REPORT_ABSENCE_NOTIFY_PATIENT en el backend: mientras esté en false, esto
    // siempre responde 'skipped'. Nunca lanza por un fallo de psima — el outcome
    // 'error' es una respuesta 200 normal, no una excepción.
    return await beHttp.post(`/api/psychologist/sessions/${sessionId}/notify-patient-absence`, {});
  },
  async psychologistFreeSlots(psychologistId: string, params?: Record<string, string | boolean | undefined>): Promise<unknown> {
    const queryParams = new URLSearchParams();
    
    // Agregar todos los parámetros dinámicamente
    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined) {
          queryParams.append(key, String(value));
        }
      });
    }
    
    const url = `/v2/psychologists/${psychologistId}/availabilities${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
    
    // Agregar headers para evitar caché HTTP 304
    return await http.get(url, undefined, {
      headers: {
        'Cache-Control': 'no-cache, no-store, must-revalidate',
        'Pragma': 'no-cache',
        'Expires': '0'
      }
    });
  },
  async deletePsychologistSlot(psychologistId: string, slotDate: string): Promise<unknown> {
    const url = `/v2/psychologists/${psychologistId}/availabilities/${encodeURIComponent(slotDate)}`;
    return await http.delete(url);
  },
  
  // External tools
  async getExternalToolUrl(tag: string): Promise<{ content?: string }> {
    return await beHttp.get(`/api/psychologist/external-tool-url`, { tag });
  },

  // Storage - Signed URL (storage queda en api-core por acuerdo)
  async getTimezoneInfo(name: string, relative?: string): Promise<{
    name: string;
    utc_offset: string;
    dst_active?: boolean;
    dst_start?: string;
    dst_end?: string;
    dst_offset?: string;
    hours_gap: number;
    countries?: string[];
  }> {
    const params: Record<string, string> = { name };
    if (relative) params.relative = relative;
    return await http.get(`/v1/timezones`, params);
  },
  async listTimezones(): Promise<Array<{
    name: string;
    utc_offset: string;
    dst_active?: boolean;
    dst_offset?: string;
    countries?: string[];
  }>> {
    return await http.get(`/v1/timezones`, {});
  },

  async getSignedUrlForUpload(input: {
    filename: string;
    contentType: string;
    size: number;
    category: 'documents' | 'images' | string;
  }): Promise<{
    url: string;
    method: string;
    headers?: Record<string, string>;
    objectPath?: string;
    expiresAt?: number;
  }> {
    return await secureHttp.post(`/v1/psi-platform/storage/get-signed-url`, {
      filename: input.filename,
      content_type: input.contentType,
      size: input.size,
      category: input.category,
    }, {
      showSuccessToast: false,
      showErrorToast: false,
    });
  },

  async listFiles(psychologistName: string): Promise<{
    files: Array<{
      name: string;
      folder: string;
      size: number;
      updatedAt: string;
      publicUrl: string;
      objectPath: string;
    }>;
  }> {
    return await secureHttp.get(`/v1/psi-platform/storage/list?name=${encodeURIComponent(psychologistName)}`, undefined, {
      showSuccessToast: false,
      showErrorToast: false,
    });
  },

  async deleteFile(objectPath: string): Promise<void> {
    await secureHttp.delete(`/v1/psi-platform/storage/file`, { object_path: objectPath }, {
      showSuccessToast: false,
      showErrorToast: false,
    });
  },

  async renameFile(objectPath: string, newName: string): Promise<{ name: string; objectPath: string; publicUrl: string }> {
    return await secureHttp.patch(`/v1/psi-platform/storage/file/rename`, { object_path: objectPath, new_name: newName }, {
      showSuccessToast: false,
      showErrorToast: false,
    });
  },

  // Patient urgency
  async setPatientUrgency(
    processId: string,
    isUrgent: boolean,
  ): Promise<unknown> {
    return await beHttp.patch(
      `/api/psychologist/patients/${encodeURIComponent(processId)}/urgency`,
      { is_urgent: isUrgent, urgent_marked_at: isUrgent ? new Date().toISOString() : null },
      { showSuccessToast: false, showErrorToast: false },
    );
  },

  // Emergency contact (Supabase en psiplatform-be). Un proceso puede tener N
  // contactos (ej. madre y padre) — cada uno se identifica por `id`.
  async listEmergencyContacts(
    processId: string,
  ): Promise<Array<{ id: string; name: string; phone: string; relationship: string }>> {
    try {
      const res = await beHttp.get<{ contacts: Array<{ id: string; name: string; phone: string; relationship: string }> }>(
        `/api/psychologist/patients/${encodeURIComponent(processId)}/emergency-contact`,
        undefined,
        { showSuccessToast: false, showErrorToast: false },
      );
      return res?.contacts ?? [];
    } catch {
      return [];
    }
  },

  async createEmergencyContact(
    processId: string,
    payload: { name: string; phone: string; relationship: string },
  ): Promise<{ id: string; name: string; phone: string; relationship: string }> {
    return await beHttp.post(
      `/api/psychologist/patients/${encodeURIComponent(processId)}/emergency-contact`,
      payload,
      { showSuccessToast: false, showErrorToast: false },
    );
  },

  async updateEmergencyContact(
    processId: string,
    id: string,
    payload: { name: string; phone: string; relationship: string },
  ): Promise<{ id: string; name: string; phone: string; relationship: string }> {
    return await beHttp.patch(
      `/api/psychologist/patients/${encodeURIComponent(processId)}/emergency-contact/${encodeURIComponent(id)}`,
      payload,
      { showSuccessToast: false, showErrorToast: false },
    );
  },

  async deleteEmergencyContact(processId: string, id: string): Promise<void> {
    await beHttp.delete(
      `/api/psychologist/patients/${encodeURIComponent(processId)}/emergency-contact/${encodeURIComponent(id)}`,
      undefined,
      { showSuccessToast: false, showErrorToast: false },
    );
  },

  // Journey bot — implementado en psiplatform-be con AiService (Claude).
  async askJourneyBot(payload: JourneyBotRequest): Promise<JourneyBotResponse> {
    return await beHttp.post(`/api/psychologist/journey/bot`, payload, {
      showSuccessToast: false,
      showErrorToast: false,
    });
  },

};


